import { z } from 'zod';
import {
  CANDIDATE_TABS,
  decisionsToApi,
  statusToApi,
  warningToApi,
  WARNING_FILTERS,
} from './vocab';

// The API allows 100 requests a minute per person, and a bulk action sends one per record.
export const PAGE_SIZES = [25, 50] as const;

/** "Confidence below" choices; the trust bar is one of them. */
export const CONFIDENCE_CEILINGS = [0.9, 0.8, 0.7, 0.5] as const;

/** The list's view, kept in the URL so it survives a refresh and can be shared. */
export const candidatesSearchSchema = z.object({
  status: z.enum(CANDIDATE_TABS).default('pending').catch('pending'),
  source: z.uuid().optional().catch(undefined),
  warning: z.enum(WARNING_FILTERS).optional().catch(undefined),
  group: z.uuid().optional().catch(undefined),
  event: z.uuid().optional().catch(undefined),
  run: z.uuid().optional().catch(undefined),
  cancelled: z.boolean().optional().catch(undefined),
  changed: z.boolean().optional().catch(undefined),
  suggested: z.literal(true).optional().catch(undefined),
  maxConfidence: z.coerce.number().gt(0).max(1).optional().catch(undefined),
  from: z.iso.date().optional().catch(undefined),
  to: z.iso.date().optional().catch(undefined),
  q: z.string().trim().min(1).optional().catch(undefined),
  page: z.coerce.number().int().min(1).default(1).catch(1),
  size: z.coerce
    .number()
    .refine((n) => (PAGE_SIZES as readonly number[]).includes(n))
    .default(PAGE_SIZES[0])
    .catch(PAGE_SIZES[0]),
});

export type CandidatesSearch = z.output<typeof candidatesSearchSchema>;

const FILTERS = [
  'q',
  'source',
  'warning',
  'group',
  'event',
  'run',
  'cancelled',
  'changed',
  'suggested',
  'maxConfidence',
  'from',
  'to',
] as const satisfies (keyof CandidatesSearch)[];

export const noFilters: Partial<CandidatesSearch> = Object.fromEntries(
  FILTERS.map((key) => [key, undefined]),
);

export const hasFilters = (search: CandidatesSearch): boolean =>
  FILTERS.some((key) => search[key] !== undefined);

/** Pending records a source change returned from publishing, and no other filter: a queue. */
export const isChangedQueue = (search: CandidatesSearch): boolean =>
  search.status === 'pending' &&
  search.changed === true &&
  FILTERS.every((key) => key === 'changed' || search[key] === undefined);

function toApiFilters(search: CandidatesSearch) {
  return {
    status: statusToApi(search.status),
    decision: decisionsToApi(search.status),
    sourceId: search.source,
    warningKind: search.warning ? warningToApi(search.warning) : undefined,
    clusterId: search.group,
    possiblyCancelled: search.cancelled,
    maxExtractionConfidence: search.maxConfidence,
    nameSearch: search.q,
    dateFrom: search.from,
    dateTo: search.to,
    changedAfterApproval: search.changed,
    suggested: search.suggested,
    linkedRaceEventId: search.event ? [search.event] : undefined,
    runId: search.run,
  };
}

export function toApiQuery(search: CandidatesSearch) {
  return { ...toApiFilters(search), pageNumber: search.page, pageSize: search.size };
}

/** The list's filters but `suggested`, which the export ignores; ids narrow it to a selection. */
export function toExportQuery(search: CandidatesSearch, ids?: string[]) {
  const { suggested: _suggested, ...filters } = toApiFilters(search);
  return { ...filters, id: ids };
}
